import React, { useState, useEffect } from 'react';
import { EmptyState } from './shared';

const GRADIENT = 'linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%)';
const GRADIENT_TEXT = {
  background: GRADIENT,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

// Script loader utility
const loadScript = (src) => {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const KeyDocuments = ({ currentReport, eDocs }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 640 && window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
      setIsTablet(window.innerWidth >= 640 && window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const rawJson = (() => {
    try {
      if (!currentReport?.json_data) return null;
      return typeof currentReport.json_data === 'string' ? JSON.parse(currentReport.json_data) : currentReport.json_data;
    } catch { return null; }
  })();

  const kd = rawJson?.key_documents || rawJson?.KEY_DOCUMENTS || {};
  const prContent = kd.PR_CONTENT || kd.pr_content || '';
  const unitEconomics = kd.UNIT_ECONOMICS || kd.unit_economics || '';
  const rawFaqs = kd.faqs || kd.FAQS || [];
  const faqs = rawFaqs.map(f => ({ question: f.FAQ_Q || f.question || '', answer: f.FAQ_A || f.answer || '' })).filter(f => f.question);

  const tabs = [
    prContent ? { label: 'Press Release', type: 'text', content: prContent } : null,
    faqs.length ? { label: 'FAQ', type: 'faq', content: faqs } : null,
    unitEconomics ? { label: 'Unit Economics', type: 'text', content: unitEconomics } : null,
  ].filter(Boolean);

  const hasJsonDocs = tabs.length > 0;
  const filteredEDocs = hasJsonDocs ? [] : eDocs.filter(d => {
    const t = (d.title || '').toLowerCase();
    return !t.includes('master deck rebuild') && !t.includes('slide specific fix') && !t.includes('slide-specific fix') && !t.includes('multi channel outreach') && !t.includes('outreach sequence');
  });

  const KeyDocCard = ({ title, subtitle, tabs }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [copied, setCopied] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const safeTab = Math.min(activeTab, tabs.length - 1);
    const current = tabs[safeTab];
    const getTabText = () => current.type === 'faq' ? current.content.map((f, i) => `Q${i + 1}: ${f.question}\nA: ${f.answer}`).join('\n\n') : typeof current.content === 'string' ? current.content : '';
    
    const handleCopy = () => {
      const text = getTabText();
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      textarea.setSelectionRange(0, 99999);
      try {
        document.execCommand('copy');
        setCopied(true); setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        if (navigator.clipboard) {
          navigator.clipboard.writeText(text).catch(() => {});
          setCopied(true); setTimeout(() => setCopied(false), 2000);
        }
      }
      document.body.removeChild(textarea);
    };
    
    const handleDownloadDocx = async () => {
      try {
        setDownloading(true);
        const text = getTabText();
        const blob = new Blob([text], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Download error:', error);
      } finally {
        setDownloading(false);
      }
    };
    
    const handleDownloadPdf = async () => {
      try {
        setDownloading(true);
        
        // Load jsPDF
        await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
        
        const { jsPDF } = window.jspdf;
        const doc = new jsPDF();
        
        const text = getTabText();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 20;
        const maxWidth = pageWidth - (margin * 2);
        
        // Add title
        doc.setFontSize(16);
        doc.setFont(undefined, 'bold');
        doc.text(title, margin, margin);
        
        // Add content
        doc.setFontSize(11);
        doc.setFont(undefined, 'normal');
        
        const lines = doc.splitTextToSize(text, maxWidth);
        let yPosition = margin + 15;
        
        lines.forEach((line) => {
          if (yPosition > pageHeight - margin) {
            doc.addPage();
            yPosition = margin;
          }
          doc.text(line, margin, yPosition);
          yPosition += 7;
        });
        
        // Save the PDF
        doc.save(`${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`);
        
      } catch (error) {
        console.error('PDF generation error:', error);
        // Fallback to text download
        handleDownloadDocx();
      } finally {
        setDownloading(false);
      }
    };

    return (
      <div style={{ 
        background: '#ffffff', 
        borderRadius: isMobile ? 14 : (isTablet ? 16 : 20), 
        border: '0.5px solid rgba(0,0,0,0.06)', 
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)', 
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}>
        {/* Card header */}
        <div style={{ 
          display: 'flex', 
          alignItems: isMobile ? 'flex-start' : 'center', 
          justifyContent: 'space-between', 
          padding: isMobile ? '14px 16px' : (isTablet ? '15px 18px' : '16px 20px'), 
          borderBottom: '0.5px solid rgba(0,0,0,0.06)', 
          flexWrap: 'wrap', 
          gap: isMobile ? 10 : 12,
          flexDirection: isMobile ? 'column' : 'row',
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: isMobile ? 12 : 14,
            width: isMobile ? '100%' : 'auto',
          }}>
            <div style={{ 
              width: isMobile ? 38 : 42, 
              height: isMobile ? 38 : 42, 
              borderRadius: isMobile ? 10 : 12, 
              background: 'linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%)', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              flexShrink: 0, 
              fontSize: isMobile ? 18 : 20 
            }}>
              📋
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ 
                fontSize: isMobile ? 14 : 15, 
                fontWeight: 700, 
                color: '#1d1d1f',
                wordBreak: 'break-word',
              }}>
                {title}
              </div>
              {subtitle && (
                <div style={{ 
                  fontSize: isMobile ? 10 : 11, 
                  color: '#aeaeb2', 
                  marginTop: 2 
                }}>
                  {subtitle}
                </div>
              )}
            </div>
          </div>
          <div style={{ 
            display: 'flex', 
            gap: isMobile ? 6 : 8,
            width: isMobile ? '100%' : 'auto',
            flexWrap: 'wrap',
          }}>
            <button 
              onClick={handleCopy} 
              style={{ 
                fontFamily: 'inherit', 
                fontSize: isMobile ? 10 : 11, 
                fontWeight: 600, 
                padding: isMobile ? '5px 10px' : '5px 12px', 
                borderRadius: isMobile ? 6 : 8, 
                border: '0.5px solid rgba(0,0,0,0.1)', 
                background: 'transparent', 
                color: '#6e6e73', 
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              📋 {copied ? 'Copied!' : 'Copy All'}
            </button>
            {/* <button 
              onClick={handleDownloadDocx}
              disabled={downloading}
              style={{ 
                fontFamily: 'inherit', 
                fontSize: isMobile ? 10 : 11, 
                fontWeight: 600, 
                padding: isMobile ? '5px 10px' : '5px 12px', 
                borderRadius: isMobile ? 6 : 8, 
                border: '0.5px solid rgba(0,0,0,0.1)', 
                background: 'transparent', 
                color: downloading ? '#aeaeb2' : '#6e6e73', 
                cursor: downloading ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap',
                opacity: downloading ? 0.6 : 1,
              }}
            >
              {downloading ? '...' : '↓ .docx'}
            </button> */}
            {/* PDF button — gradient background */}
            {/* <button 
              onClick={handleDownloadPdf}
              disabled={downloading}
              style={{ 
                fontFamily: 'inherit', 
                fontSize: isMobile ? 10 : 11, 
                fontWeight: 700, 
                padding: isMobile ? '5px 12px' : '5px 14px', 
                borderRadius: isMobile ? 6 : 8, 
                border: 'none', 
                background: GRADIENT, 
                color: '#ffffff', 
                cursor: downloading ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap',
                opacity: downloading ? 0.6 : 1,
              }}
            >
              {downloading ? '...' : '↓ .pdf'}
            </button> */}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ 
          display: 'flex', 
          background: '#f8f8fa', 
          borderBottom: '0.5px solid rgba(0,0,0,0.06)',
          overflowX: isMobile ? 'auto' : 'visible',
          WebkitOverflowScrolling: 'touch',
        }}>
          {tabs.map((tab, idx) => {
            const isActive = idx === safeTab;
            return (
              <button 
                key={idx} 
                onClick={() => setActiveTab(idx)} 
                style={{
                  fontFamily: 'inherit', 
                  fontSize: isMobile ? 11 : 12,
                  fontWeight: isActive ? 600 : 400,
                  padding: isMobile ? '8px 14px' : '10px 18px', 
                  border: 'none',
                  borderBottom: isActive ? '2px solid #004AAD' : '2px solid transparent',
                  color: isActive ? '#004AAD' : '#6e6e73',
                  background: 'transparent', 
                  cursor: 'pointer', 
                  marginBottom: '-1px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div style={{ 
          padding: isMobile ? '18px 16px' : (isTablet ? '20px 20px' : '24px 28px'), 
          maxHeight: isMobile ? 360 : 480, 
          overflowY: 'auto' 
        }}>
          {current.type === 'faq' && Array.isArray(current.content) ? (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', 
              gap: isMobile ? 10 : 12, 
              alignItems: 'start' 
            }}>
              {current.content.map((faq, fi) => (
                <div 
                  key={fi} 
                  style={{ 
                    border: '0.5px solid rgba(0,0,0,0.06)', 
                    borderRadius: isMobile ? 10 : 12, 
                    padding: isMobile ? '12px 14px' : '14px 16px', 
                    background: '#f8f8fa', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: isMobile ? 7 : 8,
                    minWidth: 0,
                  }}
                >
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: isMobile ? 8 : 10 
                  }}>
                    <span style={{ 
                      flexShrink: 0, 
                      width: isMobile ? 20 : 22, 
                      height: isMobile ? 20 : 22, 
                      borderRadius: 6, 
                      background: 'linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%)', 
                      fontSize: isMobile ? 10 : 11, 
                      fontWeight: 700, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      ...GRADIENT_TEXT 
                    }}>
                      {fi + 1}
                    </span>
                    <p style={{ 
                      fontSize: isMobile ? 12 : 13, 
                      fontWeight: 700, 
                      color: '#1d1d1f', 
                      lineHeight: 1.5, 
                      margin: 0,
                      wordBreak: 'break-word',
                    }}>
                      {(faq.question || '').replace(/^Q:\s*/i, '')}
                    </p>
                  </div>
                  <div style={{ height: 1, background: 'rgba(0,0,0,0.06)' }} />
                  <p style={{ 
                    fontSize: isMobile ? 11.5 : 12.5, 
                    color: '#6e6e73', 
                    lineHeight: 1.7, 
                    margin: 0,
                    wordBreak: 'break-word',
                  }}>
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ 
              fontFamily: "'DM Sans', sans-serif", 
              fontSize: isMobile ? 12 : 13, 
              color: '#6e6e73', 
              lineHeight: 1.85 
            }}>
              {(typeof current.content === 'string' ? current.content : '').split('\n').map((line, li) => {
                const t = line.trim();
                if (!t) return <div key={li} style={{ height: isMobile ? 10 : 12 }} />;
                if (t.endsWith(':') && t.length < 60) return (
                  <p 
                    key={li} 
                    style={{ 
                      fontSize: isMobile ? 13 : 14, 
                      fontWeight: 700, 
                      color: '#1d1d1f', 
                      margin: isMobile ? '6px 0 3px' : '8px 0 4px',
                      wordBreak: 'break-word',
                    }}
                  >
                    {t}
                  </p>
                );
                return (
                  <p 
                    key={li} 
                    style={{ 
                      margin: '2px 0', 
                      fontSize: isMobile ? 12 : 13, 
                      color: '#6e6e73', 
                      lineHeight: 1.85,
                      wordBreak: 'break-word',
                    }}
                  >
                    {t}
                  </p>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!hasJsonDocs && filteredEDocs.length === 0) return (
    <div style={{
      width: '100%',
      maxWidth: '100vw',
      overflow: 'hidden',
    }}>
      <h2 style={{ 
        fontSize: isMobile ? 18 : (isTablet ? 20 : 22), 
        fontWeight: 800, 
        letterSpacing: '-0.03em', 
        color: '#1d1d1f', 
        margin: isMobile ? '0 0 14px' : '0 0 20px', 
        fontFamily: "'Sansation', sans-serif" 
      }}>
        Key Documents
      </h2>
      <div style={{ 
        background: '#ffffff', 
        borderRadius: isMobile ? 14 : 20, 
        border: '0.5px solid rgba(0,0,0,0.06)', 
        padding: isMobile ? 20 : 28 
      }}>
        <EmptyState 
          icon="FolderOpen" 
          title="No documents found" 
          desc="Press releases, FAQ documents and other artefacts will populate from your HTML report." 
        />
      </div>
    </div>
  );

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: isMobile ? 14 : 20,
      width: '100%',
      maxWidth: '100vw',
      overflow: 'hidden',
    }}>
      <h2 style={{ 
        fontSize: isMobile ? 18 : (isTablet ? 20 : 22), 
        fontWeight: 800, 
        letterSpacing: '-0.03em', 
        color: '#1d1d1f', 
        margin: 0, 
        fontFamily: "'Sansation', sans-serif" 
      }}>
        Key Documents
      </h2>

      {hasJsonDocs && (
        <KeyDocCard 
          title="Investor Opportunity Note" 
          subtitle="Press Release + FAQ — Ready for distribution" 
          tabs={tabs} 
        />
      )}

      {filteredEDocs.map((doc, i) => {
        const [copied, setCopied] = useState(false);
        const [downloading, setDownloading] = useState(false);
        const getIcon = (t = '') => { 
          const l = t.toLowerCase(); 
          if (l.includes('press')) return '📰'; 
          if (l.includes('faq')) return '❓'; 
          if (l.includes('unit') || l.includes('econom')) return '📊'; 
          return '📋'; 
        };
        const handleCopy = () => { 
          const text = doc.faqs?.length 
            ? doc.faqs.map((f, i) => `Q${i + 1}: ${f.question}\nA: ${f.answer}`).join('\n\n') 
            : doc.content || ''; 
          navigator.clipboard?.writeText(text).catch(() => {}); 
          setCopied(true); 
          setTimeout(() => setCopied(false), 2000); 
          
          const textarea = document.createElement('textarea');
          textarea.value = text;
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.select();
          textarea.setSelectionRange(0, 99999);
          try {
            document.execCommand('copy');
            setCopied(true); setTimeout(() => setCopied(false), 2000);
          } catch (err) {
            if (navigator.clipboard) {
              navigator.clipboard.writeText(text).catch(() => {});
              setCopied(true); setTimeout(() => setCopied(false), 2000);
            }
          }
          document.body.removeChild(textarea);
        };
        
        const handleDownloadPdf = async () => {
          try {
            setDownloading(true);
            
            // Load jsPDF
            await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
            
            const { jsPDF } = window.jspdf;
            const pdf = new jsPDF();
            
            const text = doc.faqs?.length 
              ? doc.faqs.map((f, i) => `Q${i + 1}: ${f.question}\nA: ${f.answer}`).join('\n\n') 
              : doc.content || '';
            
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 20;
            const maxWidth = pageWidth - (margin * 2);
            
            // Add title
            pdf.setFontSize(16);
            pdf.setFont(undefined, 'bold');
            pdf.text(doc.title, margin, margin);
            
            // Add content
            pdf.setFontSize(11);
            pdf.setFont(undefined, 'normal');
            
            const lines = pdf.splitTextToSize(text, maxWidth);
            let yPosition = margin + 15;
            
            lines.forEach((line) => {
              if (yPosition > pageHeight - margin) {
                pdf.addPage();
                yPosition = margin;
              }
              pdf.text(line, margin, yPosition);
              yPosition += 7;
            });
            
            // Save the PDF
            pdf.save(`${doc.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`);
            
          } catch (error) {
            console.error('PDF generation error:', error);
            // Fallback to text download
            const text = doc.faqs?.length 
              ? doc.faqs.map((f, i) => `Q${i + 1}: ${f.question}\nA: ${f.answer}`).join('\n\n') 
              : doc.content || '';
            const blob = new Blob([text], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${doc.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.txt`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          } finally {
            setDownloading(false);
          }
        };
        
        return (
          <div 
            key={i} 
            style={{ 
              background: '#ffffff', 
              borderRadius: isMobile ? 14 : (isTablet ? 16 : 20), 
              border: '0.5px solid rgba(0,0,0,0.06)', 
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)', 
              overflow: 'hidden',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
            }}
          >
            <div style={{ 
              display: 'flex', 
              alignItems: isMobile ? 'flex-start' : 'center', 
              justifyContent: 'space-between', 
              padding: isMobile ? '14px 16px' : (isTablet ? '15px 18px' : '16px 20px'), 
              borderBottom: '0.5px solid rgba(0,0,0,0.06)', 
              flexWrap: 'wrap', 
              gap: isMobile ? 10 : 12,
              flexDirection: isMobile ? 'column' : 'row',
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: isMobile ? 12 : 14,
                width: isMobile ? '100%' : 'auto',
              }}>
                <div style={{ 
                  width: isMobile ? 38 : 42, 
                  height: isMobile ? 38 : 42, 
                  borderRadius: isMobile ? 10 : 12, 
                  background: '#f8f8fa', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  flexShrink: 0, 
                  fontSize: isMobile ? 18 : 20 
                }}>
                  {getIcon(doc.title)}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ 
                    fontSize: isMobile ? 14 : 15, 
                    fontWeight: 700, 
                    color: '#1d1d1f',
                    wordBreak: 'break-word',
                  }}>
                    {doc.title}
                  </div>
                  {doc.type && (
                    <div style={{ 
                      fontSize: isMobile ? 10 : 11, 
                      color: '#aeaeb2', 
                      marginTop: 2 
                    }}>
                      {doc.type}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ 
                display: 'flex', 
                gap: isMobile ? 6 : 8,
                width: isMobile ? '100%' : 'auto',
                flexWrap: 'wrap',
              }}>
                <button 
                  onClick={handleCopy} 
                  style={{ 
                    fontFamily: 'inherit', 
                    fontSize: isMobile ? 10 : 11, 
                    fontWeight: 600, 
                    padding: isMobile ? '5px 10px' : '5px 12px', 
                    borderRadius: isMobile ? 6 : 8, 
                    border: '0.5px solid rgba(0,0,0,0.1)', 
                    background: 'transparent', 
                    color: '#6e6e73', 
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  📋 {copied ? 'Copied!' : 'Copy All'}
                </button>
                <button 
                  onClick={handleDownloadPdf}
                  disabled={downloading}
                  style={{ 
                    fontFamily: 'inherit', 
                    fontSize: isMobile ? 10 : 11, 
                    fontWeight: 700, 
                    padding: isMobile ? '5px 12px' : '5px 14px', 
                    borderRadius: isMobile ? 6 : 8, 
                    border: 'none', 
                    background: GRADIENT, 
                    color: '#ffffff', 
                    cursor: downloading ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap',
                    opacity: downloading ? 0.6 : 1,
                  }}
                >
                  {downloading ? '...' : '↓ .pdf'}
                </button>
              </div>
            </div>
            {doc.faqs?.length > 0 ? (
              <div style={{ 
                padding: isMobile ? '16px 16px' : (isTablet ? '18px 20px' : '20px 24px') 
              }}>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', 
                  gap: isMobile ? 10 : 12 
                }}>
                  {doc.faqs.map((faq, fi) => (
                    <div 
                      key={fi} 
                      style={{ 
                        border: '0.5px solid rgba(0,0,0,0.06)', 
                        borderRadius: isMobile ? 10 : 12, 
                        padding: isMobile ? '12px 14px' : '14px 16px', 
                        background: '#f8f8fa', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: isMobile ? 7 : 8,
                        minWidth: 0,
                      }}
                    >
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        gap: isMobile ? 8 : 10 
                      }}>
                        <span style={{ 
                          flexShrink: 0, 
                          width: isMobile ? 20 : 22, 
                          height: isMobile ? 20 : 22, 
                          borderRadius: 6, 
                          background: 'linear-gradient(135deg, rgba(0,74,173,0.10) 0%, rgba(0,130,251,0.10) 100%)', 
                          fontSize: isMobile ? 10 : 11, 
                          fontWeight: 700, 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          ...GRADIENT_TEXT 
                        }}>
                          {fi + 1}
                        </span>
                        <p style={{ 
                          fontSize: isMobile ? 12 : 13, 
                          fontWeight: 700, 
                          color: '#1d1d1f', 
                          lineHeight: 1.5, 
                          margin: 0,
                          wordBreak: 'break-word',
                        }}>
                          {(faq.question || '').replace(/^Q:\s*/i, '')}
                        </p>
                      </div>
                      <div style={{ height: 1, background: 'rgba(0,0,0,0.06)' }} />
                      <p style={{ 
                        fontSize: isMobile ? 11.5 : 12.5, 
                        color: '#6e6e73', 
                        lineHeight: 1.7, 
                        margin: 0,
                        wordBreak: 'break-word',
                      }}>
                        {faq.answer}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ) : doc.content ? (
              <div style={{ 
                padding: isMobile ? '18px 16px' : (isTablet ? '20px 20px' : '24px 28px'), 
                maxHeight: isMobile ? 320 : 420, 
                overflowY: 'auto' 
              }}>
                <pre style={{ 
                  fontSize: isMobile ? 12 : 13, 
                  fontFamily: "'DM Sans', sans-serif", 
                  color: '#6e6e73', 
                  lineHeight: 1.85, 
                  whiteSpace: 'pre-wrap', 
                  margin: 0,
                  wordBreak: 'break-word',
                }}>
                  {doc.content}
                </pre>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

export default KeyDocuments;